/**
 * Phoenix socket auth in the `Sec-WebSocket-Protocol` header instead of the
 * URL's `?token=` (VAPT 2026-10 §6.7). A URL is copied to places a credential
 * must not go — the browser's own "WebSocket connection to … failed" console
 * error, history, proxy and access logs. The header is not.
 *
 * phoenix.js does the encoding itself for a `Socket` built with `authToken`.
 * These cover what it does not: changing the token on a live `Socket`, and the
 * meeting client's raw `WebSocket`. Signaling reads the token back in
 * `UserSocket.connect/3` (`connect_info.auth_token`).
 */

import type { Socket } from 'phoenix'

/** The prefix phoenix.js and Phoenix's websocket transport agree on. */
const AUTH_TOKEN_PREFIX = 'base64url.bearer.phx.'

/**
 * The subprotocols for `new WebSocket(url, protocols)` — exactly what phoenix.js
 * sends for `authToken`. `phoenix` is the one the server echoes back; a browser
 * fails the handshake when none of those it offered is echoed.
 *
 * Plain base64 with the padding stripped, as phoenix.js sends and the server
 * decodes. A subprotocol may not contain `=`, `+` or `/` (the constructor throws
 * a SyntaxError). Base64 of a JWT never yields `+` or `/`: only a `>`, `?`, `~`
 * or DEL byte can produce them, and none is in a JWT's alphabet.
 */
export function phoenixAuthProtocols(token: string): string[] {
  return ['phoenix', `${AUTH_TOKEN_PREFIX}${btoa(token).replace(/=/g, '')}`]
}

/**
 * Swap the token a live phoenix.js `Socket` presents on its next (re)connect.
 * phoenix.js reads `authToken` afresh on every attempt, its own reconnects
 * included; @types/phoenix types it only as a constructor option.
 */
export function setSocketAuthToken(socket: Socket, token: string): void {
  ;(socket as Socket & { authToken?: string }).authToken = token
}
